import { combine, createEffect, createEvent, createStore, sample } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { routePathNameChanged } from "@features/app/model";
import { UserEnrollmentStatus } from "@features/shared/entities/status";
import { shouldTriggerPushNotification } from "@features/push-notification/model";
import type { ILessonCompletionContract, LessonCompletionRequest } from "../model";
import type { ICurrentLessonContract } from "@features/lessons/ui/current";
import type { IUiLessonContract } from "@features/lessons/ui";
import type { ILessonFeedbackContract } from "@features/lessons/ui/lesson-feedback";
import type { ILessonsContract } from "@features/lessons/model";
import type { ICurrentCourseContract } from "@features/courses/ui/current";
import type { ICurrentTopicContract } from "@features/topics/ui/current";
import type { IDailyGoalStreakContract } from "@features/streak/ui/daily-goal-streak";
import type { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import type { IPlatformContextContract } from "@features/launch-context/model";
import type { IProductOnboardingContract } from "@features/onboarding/ui/productOnboarding";
import type { IRouterContract } from "@features/shared/router";
import type { IXpContract } from "@features/xp/model";
import {
    CompletionExit,
    CompletionExitContext,
    CompletionOutcome,
    CompletionScreen,
    LessonExit,
    NextLesson,
    RouteExit,
    nextCompletionScreen,
    nextLessonByPosition,
    resolveCompletionExit
} from "./flow";

export type { CompletionOutcome, CompletionScreen } from "./flow";

export type LessonExitStart = {
    nextLesson: NextLesson;
    completedLessonId: number;
    courseId: number;
    topicId: number;
    isTopicCompleted: boolean;
};

type CompletionEnvironment = {
    source: string | null;
    isProductV3Session: boolean;
    isProductV4Frame: boolean;
};

const NO_ENVIRONMENT: CompletionEnvironment = { source: null, isProductV3Session: false, isProductV4Frame: false };

const screenClosed = createEvent<CompletionOutcome>();
const screenEntered = createEvent<CompletionScreen>();
const flowAdvanced = createEvent<{ outcome: CompletionOutcome; next: CompletionScreen | null }>();
const completionFlowFinished = createEvent<CompletionOutcome>();
// The flow hands over to the next lesson: the transition overlay starts from this.
const lessonExitStarted = createEvent<LessonExitStart>();
const completionFlowAborted = createEvent();

const $currentScreen = createStore<CompletionScreen | null>(null)
    .on(screenEntered, (_, screen) => screen)
    .reset(completionFlowAborted, routePathNameChanged);
const $isExiting = createStore(false)
    .on(completionFlowFinished, () => true)
    .reset(completionFlowAborted, routePathNameChanged);
const $environment = createStore<CompletionEnvironment>(NO_ENVIRONMENT).reset(routePathNameChanged);
const $completionSource = $environment.map((environment) => environment.source);
const $canContinue = createStore(false);
const $hasNextLesson = createStore(false);
const $isDoneForTodayOffered = createStore(false);
const $isProductV3Flow = createStore(false);

const readCompletionEnvironmentFx = createEffect((): CompletionEnvironment => {
    if (typeof window === "undefined") return NO_ENVIRONMENT;
    const params = new URLSearchParams(window.location.search);
    return {
        source: params.get("source"),
        isProductV3Session: sessionStorage.getItem("product_v3_onboarding_active") === "true",
        isProductV4Frame: window.self !== window.top && params.get("product_v4_onboarding") === "1"
    };
});

const notifyParentFrameFx = createEffect(() => {
    window.top?.postMessage({ type: "product_v4_feedback_completed" }, "*");
});

sample({
    clock: readCompletionEnvironmentFx.doneData,
    target: $environment
});

const contract = new ModelContract(
    {
        $currentScreen,
        $isExiting,
        $canContinue,
        $hasNextLesson,
        $isDoneForTodayOffered,
        $isProductV3Flow,
        $completionSource
    },
    { screenClosed, lessonExitStarted },
    { readCompletionEnvironmentFx, notifyParentFrameFx },
    { readCompletionEnvironmentFx, notifyParentFrameFx }
);

export type ILessonCompletionFlowContract = typeof contract;

export class LessonCompletionFlowModule extends ModuleDescription<ILessonCompletionFlowContract> {
    constructor(
        public lessonCompletionModule: ILessonCompletionContract,
        public currentLessonModule: ICurrentLessonContract,
        public currentCourseModule: ICurrentCourseContract,
        public currentTopicModule: ICurrentTopicContract,
        public lessonsModule: ILessonsContract,
        public lessonUiModule: IUiLessonContract,
        public lessonFeedbackModule: ILessonFeedbackContract,
        public dailyGoalStreakModule: IDailyGoalStreakContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract,
        public platformContextModule: IPlatformContextContract,
        public productOnboardingModule: IProductOnboardingContract,
        public routerModule: IRouterContract,
        public xpModule: IXpContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        const { $activeCompletion, $completionResult } = this.lessonCompletionModule.stores;
        const { lessonCompletionRequested, lessonCompletionSettled, lessonCompletionFailed } =
            this.lessonCompletionModule.events;
        const { $streakFlow, $pushNotificationTriggerLessonIds } = this.amplitudeFlagsModule.stores;
        const { $goalComplete, $nextLessonInfo } = this.dailyGoalStreakModule.stores;
        const { currentLessonFeedbackOpened, currentLessonFeedbackHidden } = this.lessonFeedbackModule.events;

        const $askPushPermission = combine(
            $activeCompletion,
            $pushNotificationTriggerLessonIds,
            this.platformContextModule.stores.$isMetaBrowser,
            this.platformContextModule.stores.$isApp,
            (completion, triggerLessonIds, isMetaBrowser, isApp) =>
                completion !== null &&
                shouldTriggerPushNotification(completion.lessonId, triggerLessonIds) &&
                !isMetaBrowser &&
                !isApp
        );

        const $flowContext = combine({
            streakFlow: $streakFlow,
            result: $completionResult,
            askPushPermission: $askPushPermission,
            levelUp: this.xpModule.stores.$levelUp
        });

        const $exitContext = combine(
            {
                completion: $activeCompletion,
                result: $completionResult,
                isIntroLesson: this.currentLessonModule.stores.$isCurrentLessonIntro,
                isProductV3Flow: $isProductV3Flow,
                environment: $environment,
                streakFlow: $streakFlow,
                isDailyGoalComplete: $goalComplete,
                nextLessonInfo: $nextLessonInfo,
                courseLessons: this.lessonsModule.stores.$courseLessons,
                currentCourse: this.currentCourseModule.stores.$currentCourse,
                currentTopic: this.currentTopicModule.stores.$currentTopic
            },
            (state): Omit<CompletionExitContext, "outcome"> | null => {
                if (state.completion === null) return null;

                const isDailyGoalFlow = state.streakFlow === "daily_goal";
                const isFirstCompletion = state.result?.isFirstCompletion === true;
                const dailyGoalNextLesson =
                    isDailyGoalFlow && state.nextLessonInfo
                        ? {
                              courseId: state.nextLessonInfo.courseId,
                              topicId: state.nextLessonInfo.topicId,
                              lessonId: state.nextLessonInfo.lessonId
                          }
                        : null;
                const nextLesson = isFirstCompletion
                    ? (dailyGoalNextLesson ??
                      nextLessonByPosition(
                          state.courseLessons,
                          state.completion.courseId,
                          state.completion.lessonId
                      ))
                    : dailyGoalNextLesson;

                return {
                    isFirstCompletion,
                    isIntroLesson: state.isIntroLesson,
                    isProductV3Flow: state.isProductV3Flow,
                    isProductV4Frame: state.environment.isProductV4Frame,
                    isDailyGoalFlow,
                    isDailyGoalComplete: state.isDailyGoalComplete,
                    nextLesson,
                    courseId: state.completion.courseId,
                    courseType: state.currentCourse?.type ?? "course",
                    courseTags: state.currentCourse?.tags ?? [],
                    topicId: state.completion.topicId,
                    isTopicCompleted:
                        state.result?.isTopicCompleted === true ||
                        state.currentTopic?.status === UserEnrollmentStatus.Completed,
                    source: state.environment.source
                };
            }
        );

        sample({
            source: $completionResult,
            fn: (result) => result !== null,
            target: $canContinue
        });

        sample({
            source: $exitContext,
            fn: (context) =>
                context !== null && resolveCompletionExit({ ...context, outcome: "next" }).type === "lesson",
            target: $hasNextLesson
        });

        sample({
            clock: [$streakFlow, $goalComplete, $environment],
            source: { streakFlow: $streakFlow, isDailyGoalComplete: $goalComplete },
            fn: ({ streakFlow, isDailyGoalComplete }) => streakFlow === "daily_goal" && isDailyGoalComplete,
            target: $isDoneForTodayOffered
        });

        sample({
            source: {
                environment: $environment,
                isProductV3Active: this.productOnboardingModule.stores.$isProductV3OnboardingActive
            },
            fn: ({ environment, isProductV3Active }) =>
                isProductV3Active ||
                environment.isProductV3Session ||
                environment.source === "product_v3_onboarding",
            target: $isProductV3Flow
        });

        sample({
            clock: this.currentLessonModule.events.lessonFinished,
            source: $currentScreen,
            filter: (screen) => screen === null,
            fn: () => undefined,
            target: readCompletionEnvironmentFx
        });

        const completionFlowStarted = sample({
            clock: readCompletionEnvironmentFx.done,
            source: {
                lesson: this.currentLessonModule.stores.$currentLesson,
                courseId: this.currentCourseModule.stores.$currentCourseId,
                course: this.currentCourseModule.stores.$currentCourse
            },
            filter: ({ lesson, courseId }) => lesson !== null && courseId !== null,
            fn: ({ lesson, courseId, course }): LessonCompletionRequest => ({
                courseId: courseId!,
                topicId: lesson!.topic_id,
                lessonId: lesson!.id,
                isDraftCourse: course?.draft ?? false
            })
        });

        sample({
            clock: completionFlowStarted,
            target: lessonCompletionRequested
        });

        const firstScreenResolved = sample({
            clock: completionFlowStarted,
            source: $flowContext,
            fn: (context) => nextCompletionScreen(null, context)
        });

        sample({
            clock: firstScreenResolved,
            filter: (screen): screen is CompletionScreen => screen !== null,
            target: screenEntered
        });

        sample({
            clock: screenClosed,
            source: { screen: $currentScreen, context: $flowContext, isExiting: $isExiting },
            filter: ({ screen, context, isExiting }) => screen !== null && context.result !== null && !isExiting,
            fn: ({ screen, context }, outcome) => ({
                outcome,
                next: outcome === "home" ? null : nextCompletionScreen(screen, context)
            }),
            target: flowAdvanced
        });

        sample({
            clock: [$currentScreen, $flowContext],
            source: { screen: $currentScreen, context: $flowContext },
            filter: ({ screen, context }) => screen === "completing" && context.result !== null,
            fn: ({ screen, context }) => ({
                outcome: "next" as const,
                next: nextCompletionScreen(screen, context)
            }),
            target: flowAdvanced
        });

        sample({
            clock: flowAdvanced,
            filter: ({ next }) => next !== null,
            fn: ({ next }) => next as CompletionScreen,
            target: screenEntered
        });

        sample({
            clock: flowAdvanced,
            filter: ({ next }) => next === null,
            fn: ({ outcome }) => outcome,
            target: completionFlowFinished
        });

        sample({
            clock: screenEntered,
            source: $activeCompletion,
            filter: (completion, screen) => completion !== null && screen === "lessonFeedback",
            fn: (completion) => completion!.lessonId,
            target: currentLessonFeedbackOpened
        });

        sample({
            clock: screenEntered,
            filter: (screen) => screen === "levelUp",
            fn: () => undefined,
            target: this.xpModule.events.levelUpShown
        });

        const courseCompletedEntered = sample({
            clock: screenEntered,
            filter: (screen) => screen === "courseCompleted"
        });

        sample({
            clock: courseCompletedEntered,
            target: currentLessonFeedbackHidden
        });

        sample({
            clock: courseCompletedEntered,
            fn: () => true,
            target: this.currentCourseModule.events.setCourseFeebackModalVisible
        });

        sample({
            clock: lessonCompletionSettled,
            source: $streakFlow,
            filter: (streakFlow, result) => streakFlow === "daily_goal" && !result.isFirstCompletion,
            fn: (_, { courseId, lessonId }) => ({ courseId, lessonId }),
            target: this.dailyGoalStreakModule.events.nextLessonInfoRequested
        });

        sample({
            clock: lessonCompletionFailed,
            target: completionFlowAborted
        });

        const completionExitResolved = sample({
            clock: completionFlowFinished,
            source: $exitContext,
            filter: (context) => context !== null,
            fn: (context, outcome): CompletionExit => resolveCompletionExit({ ...context!, outcome })
        });

        sample({
            clock: completionExitResolved,
            filter: (exit) => exit.type === "route",
            fn: (exit) => (exit as RouteExit).url,
            target: this.routerModule.events.routerPush
        });

        sample({
            clock: completionExitResolved,
            source: { context: $exitContext, completion: $activeCompletion },
            filter: ({ context, completion }, exit) =>
                context !== null && completion !== null && exit.type === "lesson",
            fn: ({ context, completion }, exit): LessonExitStart => ({
                nextLesson: (exit as LessonExit).lesson,
                completedLessonId: completion!.lessonId,
                courseId: context!.courseId,
                topicId: context!.topicId,
                isTopicCompleted: context!.isTopicCompleted
            }),
            target: lessonExitStarted
        });

        sample({
            clock: completionExitResolved,
            filter: (exit) => exit.type === "lesson",
            fn: (exit) => {
                const { lesson, source } = exit as LessonExit;
                return { ...lesson, source };
            },
            target: this.lessonUiModule.events.startLessonFlowRequested
        });

        const parentFrameExited = sample({
            clock: completionExitResolved,
            filter: (exit) => exit.type === "parentFrame"
        });

        sample({
            clock: completionFlowFinished,
            target: currentLessonFeedbackHidden
        });

        sample({
            clock: parentFrameExited,
            target: [notifyParentFrameFx, completionFlowAborted]
        });
    }
}
